import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { addAlert, canViewWaivers, goTo, isWaiverAdmin, removeAllAlerts, setHelpPage } from "../configTdm";
import WaiverService from "./WaiverService";
import WaiverActions from "./WaiverActions";
import { API_ROUTES } from '../../js/constants';
import _ from 'lodash';
import moment from 'moment';
import WaiverConfigEditor from './ConfigEditor';
import WaiverEditor from './Editor';
import WaiverViewer from './Viewer';
import { Confirmation } from '../lib/ModalService';
import { spinnerHide, spinnerShow } from '../lib/spinner';
import AirportParser from './AirportParser';
import { getXsrfHeader } from '../../js/service/cookies';
import { PageContext } from '../../js/pages/components/page';
import axios, { AxiosResponse } from 'axios';
import { getProperty } from '../../js/fsConfig';

interface Waiver {
  [key: string]: any;
}

export const WaiverContext = createContext<any>(null);

const WaiverEditConfigView: React.FC = () => {
  const user = getProperty('session', {});
  const [waiver, setWaiver] = useState<Waiver>({});
  const [originalWaiverForDiffs, setOriginalWaiverForDiffs] = useState<Waiver>({});
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const [allWaiverVersions, setAllWaiverVersions] = useState<any[]>([]);
  const [waiverVersion, setWaiverVersion] = useState<Waiver>({});
  const [waiverConfig, setWaiverConfig] = useState<Waiver>({});
  const [originalWaiverConfig, setOriginalWaiverConfig] = useState<Waiver>({});
  const [templateAirlineCode, setTemplateAirlineCode] = useState<string>('');
  const [previousAirlineCode, setPreviousAirlineCode] = useState<string>('');
  const [newCountry, setNewCountry] = useState<{ code: string } | null>(null);
  const { waiverId, waiverVersion: routeWaiverVersion } = useParams<{ waiverId: string, waiverVersion: string }>();
  const navigate = useNavigate();
  const [airportCodeObj, setAirportCodeObj] = useState<any>({});
  const [confirmModal, setConfirmModal] = useState<any>('');
  const { pushAlert } = useContext(PageContext);
  const [authorizedAirlineCodes, setAuthorizedAirlineCodes] = useState<string[]>(user.authorizedAirlineCodes);
  const [airlineCode, setAirlineCode] = useState<string>('');

  useEffect(() => {
    if (!canViewWaivers()) {
      navigate('/home');
    }
    setHelpPage('helpWaiverList');
    initialize();
  }, []);

  const setWaiverData = (waiver: any) => {
    setWaiver(waiver);
    setOriginalWaiverForDiffs({ ...waiver });
  };

  const populateFromQueryParams = (queryParams: any) => {
    if (queryParams.airlineCode) {
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        issuingAirlineCode: queryParams.airlineCode,
      }));
    }
    if (queryParams.issueDate) {
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        publishedDate: parseDateString(queryParams.issueDate),
      }));
    }
    if (queryParams.changeEventId) {
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        changeEventId: queryParams.changeEventId,
      }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    axios.post<{ data: { id: number } }>(API_ROUTES.waiverDocuments, formData, {
      headers: {
        'X-XSRF-TOKEN': getXsrfHeader()['X-XSRF-TOKEN'],
      },
    })
      .then((response: AxiosResponse<{ data: { id: number } }>) => {
        setWaiver(prevWaiver => ({
          ...prevWaiver,
          documentId: response.data.data.id,
          documentName: file.name,
        }));
        setFormChanged(true);
      })
      .catch(() => {
        addAlert('Document upload failed');
      });
  };

  const validateWaiver = (allCriteriaPresent: boolean) => {
    removeAllAlerts();

    if (!allCriteriaPresent) {
      addAlert('Please fill in all required fields');
      return false;
    }

    if (shouldShowAirlineRelationships() && _.isEmpty(waiver.airlineRelationships)) {
      addAlert('Airline Relationships are required');
      return false;
    }

    if (waiver.ticketedOnAfter && !waiver.ticketedOnBefore) {
      addAlert('Ticketed On/Before must be provided when Ticketed On/After is provided');
      return false;
    }

    if (waiver.ticketedOnAfter && waiver.ticketedOnBefore && moment(waiver.ticketedOnAfter).isAfter(waiver.ticketedOnBefore)) {
      addAlert('Ticketed On/After must be on or before Ticketed On/Before');
      return false;
    }

    return true;
  };

  const doUpsert = (active: boolean): Promise<any> => {
    spinnerShow();
    const waiverData = formatWaiverForStorage();
    if (active) {
      waiverData.status = 'ACTIVE';
    }
    return WaiverService.upsertWaiver(waiverData).then(() => {
      spinnerHide();
      navigate('/waivers');
    }).catch((error: any) => {
      spinnerHide();
      pushAlert('danger', error?.response?.data?.error);
    });
  };

  const handleWaiverDelete = (): Promise<any> => {
    spinnerShow();
    return WaiverService.deleteWaiver(waiver.id).then(() => {
      spinnerHide();
      navigate('/waivers');
    }).catch((error: any) => {
      spinnerHide();
      pushAlert('danger', error?.response?.data?.error);
    });
  };


  const confirmQuestionableEntries = () => {
    const hasOverlyGenericAirlineRelationship = (waiver.airlineRelationships || []).some(
      (relationship: any) =>
        !relationship.bookingAirlineCodes.length ||
        !relationship.operatingAirlineCodes.length,
    );

    if (hasOverlyGenericAirlineRelationship) {
      setConfirmModal('confirmQuestionableEntries');
    } else {
      confirmSave();
    }
  };

  const confirmSave = (): any => {
    if (isConfiguration()) {
      setConfirmModal('confirmSaveConfig');
    } else if (waiver.status === 'PENDING') {
      setConfirmModal('waiverStatusPending');
    } else {
      setConfirmModal('confirmSave');
    }
  };

  const confirmChangesLost = (): any => {
    if (formHasNoChanges()) {
      return Promise.resolve();
    }
    setConfirmModal('confirmChangesLost');
  };
  const confirmChangesLostChangeAirline = (): any => {
    if (formHasNoChanges()) {
      return Promise.resolve();
    }
    setConfirmModal('confirmChangesLostChangeAirline');
  };

  const isLatestVersion = (waiver: any): boolean => {
    return waiver.version === allWaiverVersions[0].version;
  };

  const buildWaiverUrl = (waiver: any): string => {
    const baseUrl = `/waivers/${waiver.id}`;
    if (!isLatestVersion(waiver)) {
      return `${baseUrl}/version/${waiver.version}`;
    }
    return WaiverActions.canEditWaiver(waiver) ? `${baseUrl}/edit` : baseUrl;
  };

  const getWaiverVersion = (waiverVersions: any[], versionNumber: number): any => {
    if (_.isEmpty(waiverVersions)) {
      return null;
    }
    if (!versionNumber) {
      return waiverVersions[0];
    }
    return _.find(waiverVersions, waiverVersion => +versionNumber === +waiverVersion.version);
  };

  const dedupeWaiverVersions = (waiverVersions: any[]): any[] => {
    const dedupedVersions: any[] = [];
    let previousWaiver: any = {};
    _.forEach(waiverVersions, waiver => {
      if (waiver.name !== previousWaiver.name && !waiver.archived) {
        const date = _.isEmpty(dedupedVersions) ? 'Latest' : formatDate(waiver.publishedDate);
        waiver.versionDescription = `${date} - ${waiver.name}`;
        dedupedVersions.push(waiver);
        previousWaiver = waiver;
      }
    });
    return dedupedVersions;
  };

  const loadWaiver = (waiverId: string): Promise<void> => {
    spinnerShow();
    return WaiverService.getWaiverVersions(waiverId)
      .then(resp => {
        const dedupedVersions = dedupeWaiverVersions(resp.data);
        setAllWaiverVersions(dedupedVersions);
        const waiver = getWaiverVersion(resp.data, routeWaiverVersion as any);
        let waiverObj = waiver;
        if (waiver) {
          waiverObj = formatWaiverForDisplay(waiver);
          setWaiverVersion(waiver);
          setWaiver(waiverObj);
          setFormChanged(false);
        } else {
          navigate('/waivers');
        }
        spinnerHide();
        return waiverObj;
      })
      .catch(error => {
        pushAlert('danger', error?.response?.data?.error);
        spinnerHide();
      });
  };

  const loadWaiverConfig = (airlineCode: string): Promise<any> => {
    spinnerShow();
    return WaiverService.getWaiverConfiguration(airlineCode).then(resp => {
      setWaiverConfig(resp.data);
      setOriginalWaiverConfig(resp.data);
      spinnerHide();
      return resp.data;
    });
  };

  const populateWaiverConfig = (airlineCode: string): Promise<void> => {
    return loadWaiverConfig(airlineCode).then((data: any) => {
      const waiverConfigs = formatWaiverConfigForDisplay(data);
      setWaiver(waiverConfigs.waiverTemplate);
      setTemplateAirlineCode(airlineCode);
    });
  };

  const resetWaiver = (airlineCode: string, changeEventId: number | null) => {
    setPreviousAirlineCode(airlineCode);
    return {
      status: 'PENDING',
      airportSelectionType: 'byAirport',
      issuingAirlineCode: airlineCode,
      changeEventId,
    };
  };

  const populateWithDefaults = (airlineCode: string, defaultVal: any): void => {
    if (_.isEmpty(airlineCode)) {
      return;
    }
    loadWaiverConfig(airlineCode).then(response => {
      const waiverConfig = formatWaiverConfigForDisplay(response);
      const waiverTemplate = waiverConfig.waiverTemplate;
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        ...waiverTemplate,
        ticketStockInstructions: response.ticketStockInstructions,
      }));
      setOriginalWaiverForDiffs(prevWaiver => ({
        ...prevWaiver,
        ...defaultVal,
        ...waiverTemplate,
        ticketStockInstructions: response.ticketStockInstructions,
      }));
    });
  };

  const initialize = (): void => {
    window.scrollTo(0, 0);
    const defaultAirlineCode: any = _.head(authorizedAirlineCodes);
    let defaultVal = resetWaiver(defaultAirlineCode, null);
    let waiverObj = { ...defaultVal, ...waiver };
    setWaiver(waiverObj);
    if (isConfiguration()) {
      populateWaiverConfig(waiverObj.issuingAirlineCode);
    } else if (isNewWaiver()) {
      populateFromQueryParams(location.search);
      populateWithDefaults(waiverObj.issuingAirlineCode, defaultVal);
    } else if (isEdit()) {
      if (waiverId) {
        loadWaiver(waiverId)
          .then((data: any) => loadWaiverConfig(data.issuingAirlineCode))
          .then(() => populateFromQueryParams(location.search));
      }
    } else {
      if (waiverId) {
        loadWaiver(waiverId);
      }
    }
  };

  const upperCaseConverter = (val: string): string => val.toUpperCase();
  const lowerCaseConverter = (val: string): string => val.toLowerCase();

  class AirlineConfiguration {
    validate(allCriteriaPresent: boolean) {
      removeAllAlerts();

      if (!allCriteriaPresent) {
        addAlert('Please fill in all required fields');
        return false;
      }

      return true;
    }

    submit(allCriteriaPresent: boolean) {
      if (!this.validate(allCriteriaPresent)) {
        return;
      }

      confirmSave();
    }
  }

  const airlineConfigurationActions = new AirlineConfiguration();

  const isExistingWaiver = (): boolean => {
    return _.has(waiver, 'id');
  };

  const isNewWaiver = (): boolean => {
    return waiverId === 'new';
  };

  const isConfiguration = (): boolean => {
    return /configurations$/.test(location.pathname);
  };

  const isEdit = (): boolean => {
    return /edit$/.test(location.pathname);
  };

  const shouldShowAirlineCodes = (): boolean => _.isEmpty(waiver.specificFlights);

  const shouldShowAirlineRelationships = (): boolean => _.isEmpty(waiver.specificFlights);

  const shouldShowSpecificFlights = (): boolean => !_.isEmpty(waiver.specificFlights);

  const shouldShowTicketStocks = (): boolean => !isWaiverIssuedByAirline('W6', 'I5*', 'DJ*');

  const shouldShowWaiverCode = (): boolean => !isWaiverIssuedByAirline('DL');

  const shouldShowEndorsementBoxPolicy = (): boolean => !isWaiverIssuedByAirline('UA', 'NZ');

  const shouldShowCabinToCabin = (): boolean => isWaiverIssuedByAirline('DL');

  const canEditAllConfigFields = (): boolean => isWaiverAdmin();

  const shouldShowNotificationId = (): boolean => isWaiverAdmin();

  const shouldShowComments = (): boolean => isWaiverAdmin();

  const isChangeFeeCodeSupported = (): boolean => isWaiverIssuedByAirline('AS', 'DL');

  const isWaiverIssuedByAirline = (...airlines: string[]): boolean => {
    const issuingAirline = waiver.issuingAirlineCode;
    return (
      !_.isEmpty(issuingAirline) &&
      _.includes(airlines, issuingAirline.toUpperCase())
    );
  };

  const formatDate = (date: Date | null): string | null => {
    return date ? moment(date).format('YYYY-MM-DD') : null;
  };

  const formatDateTimeAsUtc = (date: Date | null): string | null => {
    return date ? moment(date).utc().format() : null;
  };

  const formatList = (list: string[]): string => {
    return !_.isEmpty(list) ? list.sort().join(', ') : '';
  };

  const csvToList = (string: string, caseConverter?: (val: string) => string): string[] => {
    if (_.isEmpty(string)) {
      return [];
    }

    if (_.isString(string)) {
      return _.chain(string.split(','))
        .map(val => val.trim())
        .map(val => (caseConverter ? caseConverter(val) : val))
        .filter(val => !_.isEmpty(val))
        .sort()
        .value();
    }

    return string;
  };

  const parseDateString = (dateString: string): Date | null => {
    if (_.isEmpty(dateString)) {
      return null;
    }

    // Add the local timezone offset back to the date to get around js wanting to display dates in the computer's timezone
    const timeZoneOffset = new Date().getTimezoneOffset();
    return moment(dateString)
      .add(timeZoneOffset, 'minutes')
      .toDate();
  };

  const parseDateStringToLocalTimezone = (dateString: string): Date | null => {
    return _.isEmpty(dateString) ? null : moment(dateString).toDate();
  };

  const formatWaiverConfigForDisplay = (waiverConfig: any): any => {
    waiverConfig.waiverTemplate = formatWaiverForDisplay(waiverConfig.waiverTemplate);
    waiverConfig.waiverKeywords = formatList(waiverConfig.waiverKeywords);
    return waiverConfig;
  };

  const formatWaiverForDisplay = (waiver: any): any => {
    waiver.airportCodes = formatList(waiver.airportCodes);
    waiver.ticketStocks = formatList(waiver.ticketStocks);
    waiver.countryCodes = formatList(waiver.countryCodes);

    if (waiver.systemWide) {
      waiver.airportSelectionType = 'systemWide';
    } else {
      waiver.airportSelectionType = _.isEmpty(waiver.countryCodes) ? 'byAirport' : 'byCountry';
    }

    if (waiver.domesticFlightsOnly === true) {
      waiver.domesticOrInternationalFlights = 'domesticFlights';
    } else if (waiver.internationalFlightsOnly === true) {
      waiver.domesticOrInternationalFlights = 'internationalFlights';
    } else {
      waiver.allFlights = true;
      waiver.domesticOrInternationalFlights = 'allFlights';
    }

    waiver.publishedDate = parseDateString(waiver.publishedDate);
    waiver.firstEffectiveDate = parseDateString(getFirstNonNull(waiver.firstEffectiveDate, _.head(waiver.effectiveTravelDates)));
    waiver.lastEffectiveDate = parseDateString(getFirstNonNull(waiver.lastEffectiveDate, _.last(waiver.effectiveTravelDates)));
    waiver.ticketedOnAfter = parseDateString(waiver.ticketedOnAfter);
    waiver.ticketedOnBefore = parseDateString(waiver.ticketedOnBefore);
    waiver.reIssuedTicketOnBefore = parseDateString(waiver.reIssuedTicketOnBefore);
    waiver.firstNewTravelDate = parseDateString(waiver.firstNewTravelDate);
    waiver.lastNewTravelDate = parseDateString(waiver.lastNewTravelDate);

    waiver.specificFlights = _.map(waiver.specificFlights || [], (flightCriteria: any) => {
      return {
        ...flightCriteria,
        firstDepartureDate: parseDateString(flightCriteria.firstDepartureDate),
        lastDepartureDate: parseDateString(flightCriteria.lastDepartureDate),
      };
    });

    return waiver;
  };

  const getFirstNonNull = (...args: any[]): any => {
    for (let i = 0; i < args.length; i++) {
      if (args[i] != null) {
        return args[i];
      }
    }
    return null;
  };

  const markFormAsDirty = (): void => {
    // Assuming you have a form reference or state to mark the form as dirty
    setFormChanged(true);
  };

  const updateWaiverField = (field: string, newValue: any): void => {
    setWaiver(prevWaiver => ({
      ...prevWaiver,
      [field]: newValue,
    }));
    markFormAsDirty();
  };

  const updateSpecificFlights = (newFlightCriteria: any[]): void => {
    updateWaiverField('specificFlights', newFlightCriteria);

    if (newFlightCriteria.length > 0) {
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        firstEffectiveDate: _.minBy(newFlightCriteria, 'firstDepartureDate').firstDepartureDate,
        lastEffectiveDate: _.maxBy(newFlightCriteria, 'lastDepartureDate').lastDepartureDate,
      }));
    }
  };

  const formatWaiverConfigForStorage = (): any => {
    // We don't want to touch the model backing the view (since we modify field structure) as that can cause error messages
    const waiverConfigCopy = JSON.parse(JSON.stringify(waiverConfig));
    waiverConfigCopy.waiverKeywords = csvToList(waiverConfigCopy.waiverKeywords, lowerCaseConverter);
    return waiverConfigCopy;
  };

  const formatWaiverForStorage = (): any => {
    const waiverCopy = JSON.parse(JSON.stringify(waiver));
    waiverCopy.changeEventId = parseInt(waiverCopy.changeEventId) || undefined;
    waiverCopy.publishedDate = formatDate(waiverCopy.publishedDate);
    waiverCopy.issuingAirlineCode = waiverCopy.issuingAirlineCode.toUpperCase();
    waiverCopy.marketingAirlineCodes = csvToList(
      waiverCopy.marketingAirlineCodes,
      upperCaseConverter,
    );
    waiverCopy.specificFlights = _.map(
      waiverCopy.specificFlights || [],
      (flightCriteria: any) => {
        return {
          ...flightCriteria,
          firstDepartureDate: formatDate(flightCriteria.firstDepartureDate),
          lastDepartureDate: formatDate(flightCriteria.lastDepartureDate),
        };
      },
    );
    waiverCopy.airportCodes = csvToList(waiverCopy.airportCodes, upperCaseConverter);
    waiverCopy.countryCodes = csvToList(waiverCopy.countryCodes, upperCaseConverter);
    waiverCopy.ticketStocks = csvToList(waiverCopy.ticketStocks);
    waiverCopy.ticketedOnAfter = formatDate(waiverCopy.ticketedOnAfter);
    waiverCopy.ticketedOnBefore = formatDate(waiverCopy.ticketedOnBefore);
    waiverCopy.reIssuedTicketOnBefore = formatDate(waiverCopy.reIssuedTicketOnBefore);
    waiverCopy.firstNewTravelDate = formatDate(waiverCopy.firstNewTravelDate);
    waiverCopy.lastNewTravelDate = formatDate(waiverCopy.lastNewTravelDate);

    waiverCopy.firstEffectiveDate = formatDate(waiverCopy.firstEffectiveDate);
    waiverCopy.lastEffectiveDate = formatDate(waiverCopy.lastEffectiveDate);

    waiverCopy.systemWide = airportsSystemWide();
    if (airportsByAirport() || waiverCopy.systemWide) {
      waiverCopy.countryCodes = null;
    }
    if (airportsByCountry() || waiverCopy.systemWide) {
      waiverCopy.airportCodes = null;
    }

    waiverCopy.allFlights = isAllFlights();
    waiverCopy.domesticFlightsOnly = isDomesticFlightsOnly();
    waiverCopy.internationalFlightsOnly = isInternationalFlightsOnly();

    if (!_.isEmpty(waiverCopy.specificFlights)) {
      waiverCopy.systemWide = false;
      waiverCopy.countryCodes = null;
      waiverCopy.airportCodes = null;
      waiverCopy.airlineRelationships = null;
    }

    waiverCopy.effectiveTravelDates = [];
    return waiverCopy;
  };

  const submit = (allCriteriaPresent: boolean): void => {
    if (isConfiguration()) {
      airlineConfigurationActions.submit(allCriteriaPresent);
      return;
    }

    if (!validateWaiver(allCriteriaPresent)) {
      return;
    }

    confirmQuestionableEntries();
  };

  const editDefaultDoUpsert = () => {
    spinnerShow();
    const waiverConfigForStorage = formatWaiverConfigForStorage();
    const waiverForStorage = formatWaiverForStorage();
    const waiverConfig = Object.assign({}, waiverConfigForStorage, {
      waiverTemplate: waiverForStorage,
    });
    return WaiverService.upsertWaiverConfiguration(waiverConfig).then(() => {
      spinnerHide();
      navigate('/waivers');
    }).catch((error: any) => {
      spinnerHide();
      setConfirmModal('')
      addAlert(error?.response?.data?.error || 'Save Config failed');
    });
  }

  const deleteWaiver = (): void => {
    if (canDeleteWaiver()) {
      setConfirmModal('confirmDelete');
    }
  };

  const canDeleteWaiver = (): boolean => {
    return _.isNumber(waiver.id) && waiver.status === 'PENDING';
  };

  const hasWaiverDocument = (): boolean => {
    return WaiverActions.hasWaiverDocument(waiver as any);
  };

  const documentLink = (): string => {
    return WaiverActions.documentLink(waiver as any);
  };

  const removeDocument = (): void => {
    setWaiver(prevWaiver => ({
      ...prevWaiver,
      documentId: null,
      documentName: null,
    }));
    setFormChanged(true);
  };

  const hasPolicyLinks = (): boolean => {
    return !_.isEmpty(waiver.policyLinks);
  };

  const editAirports = (): void => {
    const currentAirportCodes = csvToList(waiver.airportCodes);
    setAirportCodeObj({ currentAirportCodes: currentAirportCodes, isAirport: true });
  };

  const affectedAirports = (airportCodes: any) => {
    setWaiver(prevWaiver => ({
      ...prevWaiver,
      airportCodes: formatList(airportCodes),
    }));
    setFormChanged(true);
  }

  const airportsByAirport = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.airportSelectionType === 'byAirport';
  };

  const airportsByCountry = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.airportSelectionType === 'byCountry';
  };

  const airportsSystemWide = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.airportSelectionType === 'systemWide';
  };

  const isAllFlights = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.domesticOrInternationalFlights === 'allFlights';
  };

  const isDomesticFlightsOnly = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.domesticOrInternationalFlights === 'domesticFlights';
  };

  const isInternationalFlightsOnly = (): boolean => {
    return _.isEmpty(waiver.specificFlights) && waiver.domesticOrInternationalFlights === 'internationalFlights';
  };

  const addCountry = (item: any): void => {
    if (item) {
      const newCountryCode = item.code;
      const currentCountryCodes = waiver.countryCodes || '';
      setWaiver(prevWaiver => ({
        ...prevWaiver,
        countryCodes: _.isEmpty(currentCountryCodes)
          ? newCountryCode : currentCountryCodes.trim() + ', ' + newCountryCode,
      }));
      setNewCountry(null);
    }
  };

  const cancelChanges = (): void => {
    confirmChangesLost().then(() => {
      goTo('/waivers');
    });
  };

  const changeAirline = (airlineCode: string): void => {
    let defaultVal = resetWaiver(airlineCode, waiver.changeEventId);
    setWaiver({ ...defaultVal, ...waiver });
    isConfiguration()
      ? populateWaiverConfig(airlineCode)
      : populateWithDefaults(airlineCode, defaultVal);
  };

  const changeIssuingAirline = (airlineCode: any): void => {
    setFormChanged(true);
    if (isExistingWaiver()) {
      return;
    }

    if(isConfiguration()) {
      const isAirlineCodeOnlyChange = _.isEqual(waiverConfig, originalWaiverConfig)
      if (isAirlineCodeOnlyChange) {
        changeAirline(airlineCode);
        return;
      }
    }

    const isAirlineCodeOnlyChange = _.isEqual(waiver, {
      ...originalWaiverForDiffs,
      issuingAirlineCode: waiver.issuingAirlineCode,
    });

    if (isAirlineCodeOnlyChange) {
      changeAirline(airlineCode);
      return;
    }
    setAirlineCode(airlineCode);
    confirmChangesLostChangeAirline();
  };

  const setStartDate = (startDate: Date): void => {
    setWaiver(prevWaiver => {
      const updatedWaiver: any = { ...prevWaiver, firstEffectiveDate: startDate };
      if (
        !updatedWaiver.lastEffectiveDate ||
        moment(updatedWaiver.lastEffectiveDate).isBefore(startDate)
      ) {
        updatedWaiver.lastEffectiveDate = startDate;
      }
      return updatedWaiver;
    });
    markFormAsDirty();
  };

  const setEndDate = (endDate: Date): void => {
    setWaiver(prevWaiver => {
      const updatedWaiver: any = { ...prevWaiver, lastEffectiveDate: endDate };
      if (
        !updatedWaiver.firstEffectiveDate ||
        moment(updatedWaiver.firstEffectiveDate).isAfter(endDate)
      ) {
        updatedWaiver.firstEffectiveDate = endDate;
      }
      return updatedWaiver;
    });
    markFormAsDirty();
  };

  const changeWaiverVersion = (getVersion: any): void => {
    let selectionList = getWaiverVersion(allWaiverVersions, getVersion);
    confirmChangesLost().then(() => {
      const url = buildWaiverUrl(selectionList);
      navigate(url);
    });
    setWaiverVersion(selectionList);
  };

  const formHasNoChanges = (): boolean => {
    return !formChanged;
  };

  const waiverContextVal = {
    waiver,
    originalWaiverForDiffs,
    formChanged,
    allWaiverVersions,
    waiverVersion,
    waiverConfig,
    templateAirlineCode,
    previousAirlineCode,
    newCountry,
    waiverId,
    location,
    authorizedAirlineCodes,
    setWaiverData,
    populateFromQueryParams,
    validateWaiver,
    doUpsert,
    confirmQuestionableEntries,
    confirmSave,
    confirmChangesLost,
    isLatestVersion,
    buildWaiverUrl,
    getWaiverVersion,
    dedupeWaiverVersions,
    loadWaiver,
    loadWaiverConfig,
    populateWaiverConfig,
    resetWaiver,
    populateWithDefaults,
    initialize,
    upperCaseConverter,
    lowerCaseConverter,
    airlineConfigurationActions,
    isExistingWaiver,
    isNewWaiver,
    isConfiguration,
    isEdit,
    shouldShowAirlineCodes,
    shouldShowAirlineRelationships,
    shouldShowSpecificFlights,
    shouldShowTicketStocks,
    shouldShowWaiverCode,
    shouldShowEndorsementBoxPolicy,
    shouldShowCabinToCabin,
    canEditAllConfigFields,
    shouldShowNotificationId,
    shouldShowComments,
    isChangeFeeCodeSupported,
    isWaiverIssuedByAirline,
    formatDate,
    formatDateTimeAsUtc,
    formatList,
    csvToList,
    parseDateString,
    parseDateStringToLocalTimezone,
    formatWaiverConfigForDisplay,
    formatWaiverForDisplay,
    getFirstNonNull,
    markFormAsDirty,
    updateWaiverField,
    updateSpecificFlights,
    formatWaiverConfigForStorage,
    formatWaiverForStorage,
    submit,
    deleteWaiver,
    canDeleteWaiver,
    hasWaiverDocument,
    documentLink,
    removeDocument,
    hasPolicyLinks,
    editAirports,
    airportsByAirport,
    airportsByCountry,
    airportsSystemWide,
    isAllFlights,
    isDomesticFlightsOnly,
    isInternationalFlightsOnly,
    addCountry,
    cancelChanges,
    changeIssuingAirline,
    setStartDate,
    setEndDate,
    changeWaiverVersion,
    formHasNoChanges,
    setWaiver,
    setWaiverVersion,
    affectedAirports,
    airportCodeObj,
    setNewCountry,
    setWaiverConfig,
    handleFileChange
  };

  let waiverTemplate = <WaiverViewer></WaiverViewer>;
  if (location.pathname === '/waivers/configurations') {
    waiverTemplate = <WaiverConfigEditor></WaiverConfigEditor>;
  } else if (location.pathname === '/waivers/:waiverId/edit'.replace(':waiverId', waiver.id) || location.pathname === '/waivers/new/edit') {
    waiverTemplate = <WaiverEditor></WaiverEditor>;
  }

  let confirmationTemplate = <></>;
  if (confirmModal === 'confirmChangesLost') {
    confirmationTemplate = <Confirmation message='Changes you have made will not be saved. Continue?' optionOverrides={{
      title: 'Unsaved Changes',
      onSubmit: () => { removeAllAlerts(); navigate('/waivers'); },
      cancel: () => setConfirmModal('')
    }}></Confirmation>;
  } else if (confirmModal === 'confirmChangesLostChangeAirline') {
    confirmationTemplate = <Confirmation message='Changes you have made will not be saved. Continue?' optionOverrides={{
      title: 'Unsaved Changes',
      onSubmit: () => { setConfirmModal(''); changeAirline(airlineCode); },
      cancel: () => setConfirmModal('')
    }}></Confirmation>;
  } else if (confirmModal === 'confirmSave') {
    confirmationTemplate = <Confirmation message='This is an active waiver. Are you sure you want to apply the updated information?'
      optionOverrides={{ onSubmit: () => doUpsert(), cancel: () => setConfirmModal('') }}></Confirmation>;
  } else if (confirmModal === 'confirmSaveConfig') {
    confirmationTemplate = <Confirmation message='Are you sure you want to save changes to the default waiver setting?'
      optionOverrides={{ cancel: () => setConfirmModal(''), onSubmit: () => editDefaultDoUpsert() }}></Confirmation>;
  } else if (confirmModal === 'confirmQuestionableEntries') {
    confirmationTemplate = <Confirmation message={
      `You included an airline relationship that either has booking/marketing airlines without any 
        associated operating airlines, or operating airlines without any associated booking/marketing 
        airlines. By doing this, you may drastically increase the number of trip matches and alerts 
        generated by our system. Are you certain you want to continue?`} 
        optionOverrides={{ 
          cancel: () => setConfirmModal(''), 
          onSubmit: () => { 
            setConfirmModal(''); 
            confirmSave(); 
          } 
        }}
    ></Confirmation>;
  } else if (confirmModal === 'waiverStatusPending') {
    confirmationTemplate = <Confirmation message='This waiver is not yet activated. Do you want to activate it now?'
      optionOverrides={{
        title: 'Activate Waiver?', onSubmit: () => {
          setConfirmModal('');
          doUpsert(true);
        }, cancel: () => {
          setConfirmModal('');
          doUpsert(false);
        }
      }}></Confirmation>;
  } else if (confirmModal === 'confirmDelete') {
    confirmationTemplate = <Confirmation message='Are you sure you want to delete this waiver? This action cannot be undone.'
      optionOverrides={{
        title: 'Delete Waiver?', onSubmit: () => {
          setConfirmModal('');
          handleWaiverDelete();
        }, cancel: () => {
          setConfirmModal('');
        }
      }}
    ></Confirmation>
  }
  return (<>
    {confirmationTemplate}
    {airportCodeObj.isAirport && <AirportParser airportCodeObj={airportCodeObj} onClose={(airports) => {
      setAirportCodeObj({ currentAirportCodes: airports, isAirport: false });
      affectedAirports(airports);
    }}></AirportParser>}
    <WaiverContext.Provider value={waiverContextVal}>
      {waiverTemplate}
    </WaiverContext.Provider>
  </>);
};

export default WaiverEditConfigView;